import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Download,
  ExternalLink,
} from 'lucide-react';
import type { FilingObligationDto, PreSessionReportPreview } from '@signalvote/shared';
import { ErrorCode } from '@signalvote/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PendingButton } from '@/components/ui/pending-button';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import {
  useConfirmSessionDate,
  useGenerateObligationReport,
  usePreSessionPreview,
  ObligationGenerateError,
} from '@/hooks/queries/useFinanceReports';
import { formatAmount, formatDate } from '../utils';
import { triggerBrowserDownload } from './downloadHelpers';
import { deriveCta } from '../close/preSessionCta';

// ============================================================================
// Virginia §24.2-947.11 pre-session large-contribution report modal (GitHub
// #164, ADR-039). A sibling of FilePreflightModal — the app's established
// review-before-file surface — so the treasurer stays on /close instead of being
// navigated to a separate screen. Fetches the assembly plan on open and walks the
// treasurer down a single column:
//   1. confirm the General Assembly session date (GATES generation)
//   2. what will file (scope)
//   3. Fix before filing (blocking gaps — deep-link out to the contribution)
//   4. Filed without these (excluded gifts)
//   5. File by hand in COMET (in-window loans — v1 doesn't auto-include loans)
//   6. a state-driven primary CTA
// Until the session date is confirmed, sections 2–6 are de-emphasized and Generate
// is disabled. All VA-specific copy (the constitutional cite, "second Wednesday")
// lives here so it can never leak to AL/GA/LA decks.
// ============================================================================

/** The official General Assembly session-schedule page — lets a treasurer verify
 *  the prefill or look up a special-session convening date. */
const GA_SESSION_CALENDAR_URL = 'https://virginiageneralassembly.gov/';

/**
 * Translate a named generate refusal to plain treasurer language at the render
 * boundary — no engineer identifier ever reaches the UI. The CTA gating prevents
 * most of these; this is the defense-in-depth fallback (mirrors the
 * MarkFiledInPortalError → copy mapping pattern).
 */
function generateErrorCopy(err: unknown): string {
  if (err instanceof ObligationGenerateError) {
    switch (err.code) {
      case ErrorCode.PreSessionSessionDateNotConfirmed:
        return 'Confirm the General Assembly session date before generating the report.';
      case ErrorCode.PreSessionWindowStillOpen:
        return 'The disclosure window has not closed yet. You can generate once it does.';
      case ErrorCode.PreSessionNoQualifyingDonors:
        return 'No donor crossed $1,000 in the window, so there is nothing to file.';
      case null:
      default:
        return err.message || 'Could not generate the report. Try again.';
    }
  }
  return err instanceof Error ? err.message : 'Could not generate the report. Try again.';
}

/** Local-calendar-day → `yyyy-MM-dd` (timezone-stable: reads the Date's own
 *  local Y/M/D, so a picker's local-midnight selection never shifts a day). */
function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

interface PreSessionReportModalProps {
  open: boolean;
  obligation: FilingObligationDto;
  /** Closes the modal — caller still controls open state. */
  onClose: () => void;
}

export function PreSessionReportModal({ open, obligation, onClose }: PreSessionReportModalProps) {
  const navigate = useNavigate();
  const { data: preview, isLoading, error } = usePreSessionPreview(open ? obligation.id : null);
  const { confirmSessionDate } = useConfirmSessionDate();
  const { generateObligationReport } = useGenerateObligationReport();

  const [busy, setBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);
  // Post-generate success (kept local — the obligation prop won't update in place;
  // the deck card flips to "Mark as reported" off the invalidated obligations query).
  const [generated, setGenerated] = useState(false);

  // The picker's own accept/override/edit state lives inside PreSessionDateGate;
  // this only fires the attestation and reports success so the gate can collapse
  // its edit view on success but keep it open (with the error) on failure. An
  // omitted date accepts the prefill.
  async function handleConfirmDate(sessionStartDate?: string): Promise<boolean> {
    if (busy) return false;
    setBusy(true);
    setConfirmError(null);
    try {
      await confirmSessionDate({ obligationId: obligation.id, ...(sessionStartDate ? { sessionStartDate } : {}) });
      return true;
    } catch (e) {
      setConfirmError(e instanceof Error ? e.message : 'Could not confirm the session date.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function handleGenerate() {
    if (busy) return;
    setBusy(true);
    setGenerateError(null);
    try {
      const result = await generateObligationReport(obligation.id);
      triggerBrowserDownload(result.fileBlob, result.fileName);
      setGenerated(true);
    } catch (e) {
      setGenerateError(generateErrorCopy(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0">
        <DialogHeader>
          <DialogTitle>Pre-session large-contribution report</DialogTitle>
          <DialogDescription>
            Virginia requires this report by January 15 for every donor who gave more than $1,000
            before the General Assembly convened. Confirm the session date, review what will file,
            then generate the file to upload in COMET.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          {isLoading && (
            <div className="text-sm text-muted-foreground" data-testid="pre-session-loading">
              Loading the report preview…
            </div>
          )}

          {error && (
            <div
              className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
              data-testid="pre-session-error"
            >
              Couldn't load the report preview. There was an error fetching the data.
            </div>
          )}

          {preview && !error && (
            <PreSessionBody
              preview={preview}
              generated={generated}
              busy={busy}
              confirmError={confirmError}
              generatedAt={obligation.generatedAt}
              onConfirmDate={handleConfirmDate}
              onBlockingClick={(contributionId) => {
                void navigate({
                  to: '/admin/finance/contributions/$contributionId',
                  params: { contributionId },
                });
                onClose();
              }}
            />
          )}

          {generateError && (
            <div
              className="rounded-md border border-destructive bg-destructive/10 p-2 text-sm text-destructive"
              data-testid="pre-session-generate-error"
              role="alert"
            >
              {generateError}
            </div>
          )}
        </div>

        <DialogFooter className="mt-4 flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {generated ? 'Done' : 'Cancel'}
          </Button>
          {preview && !generated && (
            <PreSessionCtaButton
              preview={preview}
              generatedAt={obligation.generatedAt}
              busy={busy}
              onGenerate={() => void handleGenerate()}
            />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── The single-column body ──────────────────────────────────────────────────

function PreSessionBody({
  preview,
  generated,
  busy,
  confirmError,
  generatedAt,
  onConfirmDate,
  onBlockingClick,
}: {
  preview: PreSessionReportPreview;
  generated: boolean;
  busy: boolean;
  confirmError: string | null;
  generatedAt: string | null;
  /** Fires the attestation; resolves true on success. An omitted date accepts the prefill. */
  onConfirmDate: (sessionStartDate?: string) => Promise<boolean>;
  onBlockingClick: (contributionId: string) => void;
}) {
  return (
    <>
      {/* 1 — Confirm the session date (the gate). */}
      <PreSessionDateGate
        preview={preview}
        generatedAt={generatedAt}
        busy={busy}
        confirmError={confirmError}
        onConfirm={onConfirmDate}
      />

      {/* Sections 2–5 are de-emphasized until the session date is confirmed —
          the treasurer can preview the scope but the numbers only firm up once the
          window is pinned to a confirmed date. */}
      <div
        className={cn(
          'space-y-4',
          !preview.sessionDateConfirmed && 'pointer-events-none opacity-50',
        )}
        aria-disabled={!preview.sessionDateConfirmed || undefined}
        data-testid="pre-session-downstream"
      >
        {/* 2 — What will file. */}
        <section className="rounded-md border bg-muted/30 p-3 text-sm">
          <p className="font-medium text-slate-900">What will file</p>
          <p data-testid="pre-session-scope" className="text-muted-foreground">
            {preview.qualifyingDonorCount}{' '}
            {preview.qualifyingDonorCount === 1 ? 'donor' : 'donors'} over $1,000 ·{' '}
            {preview.includedGiftCount} {preview.includedGiftCount === 1 ? 'gift' : 'gifts'} will file
          </p>
        </section>

        {/* 3 — Fix before filing (blocking gaps, deep-link out). */}
        {preview.blockingGaps.length > 0 && (
          <section
            data-testid="pre-session-blocking"
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
          >
            <p className="flex items-center gap-1.5 font-semibold text-amber-900">
              <AlertTriangle className="h-4 w-4" aria-hidden />
              Fix before filing
            </p>
            <p className="mt-0.5 text-xs text-amber-800">
              These gifts can't be filed until you resolve them. Select one to fix it.
            </p>
            <ul className="mt-2 space-y-1">
              {preview.blockingGaps.map((g) => (
                <li key={g.contributionId}>
                  <button
                    type="button"
                    data-testid={`pre-session-blocking-${g.contributionId}`}
                    onClick={() => onBlockingClick(g.contributionId)}
                    className="flex w-full items-center justify-between gap-3 rounded border border-amber-200 bg-white px-2 py-1.5 text-left hover:border-amber-400"
                  >
                    <span className="font-medium text-slate-900">{g.donorName ?? 'Unnamed donor'}</span>
                    <span className="text-xs text-amber-800">{g.reason}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* 4 — Filed without these (excluded gifts). */}
        {preview.excludedGifts.length > 0 && (
          <section
            data-testid="pre-session-excluded"
            className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm"
          >
            <p className="font-semibold text-rose-900">Filed without these</p>
            <p className="mt-0.5 text-xs text-rose-800">
              These gifts can't be filed yet and will be left off this report.
            </p>
            <ul className="mt-2 space-y-1">
              {preview.excludedGifts.map((g) => (
                <li
                  key={g.contributionId}
                  className="flex items-center justify-between gap-3 rounded border border-rose-200 bg-white px-2 py-1.5"
                >
                  <span className="min-w-0">
                    <span className="font-medium text-slate-900">{g.donorName ?? 'Unnamed donor'}</span>
                    <span className="block text-xs text-rose-800">{g.reason}</span>
                  </span>
                  <span className="shrink-0 font-semibold tabular-nums text-slate-900">
                    {formatAmount(g.amount)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* 5 — File by hand in COMET (loans — v1 doesn't auto-include them). */}
        {preview.loanAlerts.length > 0 && (
          <section
            data-testid="pre-session-loans"
            className="rounded-md border bg-muted/30 p-3 text-sm"
          >
            <p className="font-semibold text-slate-900">File by hand in COMET</p>
            <p className="mt-0.5 text-xs text-slate-600">
              This report doesn't include loans automatically. File each in-window loan yourself in
              COMET.
            </p>
            <ul className="mt-2 space-y-1">
              {preview.loanAlerts.map((l) => (
                <li
                  key={l.loanId}
                  className="flex items-center justify-between gap-3 rounded border bg-white px-2 py-1.5"
                >
                  <span className="font-medium text-slate-900">{l.lenderName ?? 'Unnamed lender'}</span>
                  <span className="shrink-0 font-semibold tabular-nums text-slate-900">
                    {formatAmount(l.amount)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {/* Post-generate: the supplemental reminder so the double appearance on the
          next periodic report is never a surprise (ADR-039). */}
      {generated && (
        <div
          data-testid="pre-session-generated-note"
          className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900"
        >
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            Report generated and downloaded. Upload it in COMET, then mark it reported on your close
            page. These contributions still appear on your next periodic report — this filing is
            supplemental, so marking it reported doesn't remove them.
          </span>
        </div>
      )}
    </>
  );
}

// ── Section 1: the confirm-session-date gate ────────────────────────────────
// Owns its own accept-first / override / edit state so the picker's UI and the
// state it drives stay colocated (the parent only supplies `onConfirm`). Accept-
// first: a primary "Accept — convenes {prefill}" plus a secondary "Use a
// different date" that reveals a free Calendar. Editable until the file is
// generated.

function PreSessionDateGate({
  preview,
  generatedAt,
  busy,
  confirmError,
  onConfirm,
}: {
  preview: PreSessionReportPreview;
  generatedAt: string | null;
  busy: boolean;
  confirmError: string | null;
  /** Fires the attestation; resolves true on success. An omitted date accepts the prefill. */
  onConfirm: (sessionStartDate?: string) => Promise<boolean>;
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [pickedDate, setPickedDate] = useState<Date | undefined>(undefined);
  // Editing an already-confirmed date (before generation) reveals the controls again.
  const [editingDate, setEditingDate] = useState(false);

  // Collapse the edit/override controls on a SUCCESSFUL confirm only — a failed
  // confirm keeps them open with the error visible (the error lives in this block).
  const confirmThenReset = async (sessionStartDate?: string) => {
    if (await onConfirm(sessionStartDate)) {
      setShowPicker(false);
      setPickedDate(undefined);
      setEditingDate(false);
    }
  };

  // The confirmed date is editable until the file is generated. Once confirmed,
  // show the summary; the accept/override controls only reappear on Edit.
  const showDateControls = !preview.sessionDateConfirmed || editingDate;
  const overrode =
    preview.sessionDateConfirmed
    && preview.sessionStartConfirmed != null
    && preview.sessionStartConfirmed !== preview.sessionStartPrefill;

  return (
    <section className="space-y-2 rounded-md border bg-muted/30 p-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <CalendarDays className="h-4 w-4 text-slate-500" aria-hidden />
        Confirm the General Assembly session date
      </div>

      {preview.sessionDateConfirmed && !editingDate && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
          <span data-testid="pre-session-session-confirmed" className="font-medium">
            Session convenes {formatDate(preview.sessionStartConfirmed ?? preview.sessionStartPrefill)}
          </span>
          {/* Editable until the file is generated. */}
          {generatedAt == null && (
            <button
              type="button"
              data-testid="pre-session-edit-date"
              onClick={() => setEditingDate(true)}
              className="text-xs font-medium text-blue-700 underline hover:text-blue-900"
            >
              Change
            </button>
          )}
        </div>
      )}

      {overrode && (
        <p data-testid="pre-session-window-override-note" className="text-xs text-amber-800">
          Window now runs {formatDate(preview.windowStart)} to {formatDate(preview.windowEnd)}.
        </p>
      )}

      {showDateControls && (
        <div className="space-y-2">
          {!showPicker ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                data-testid="pre-session-accept-prefill"
                disabled={busy}
                onClick={() => void confirmThenReset()}
                className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                Accept — convenes {formatDate(preview.sessionStartPrefill)}
              </button>
              <button
                type="button"
                data-testid="pre-session-use-different-date"
                disabled={busy}
                onClick={() => setShowPicker(true)}
                className="rounded px-2 py-1.5 text-xs font-medium text-slate-600 underline hover:text-slate-900 disabled:opacity-60"
              >
                Use a different date
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <Calendar mode="single" selected={pickedDate} onSelect={setPickedDate} />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="pre-session-confirm-picked-date"
                  disabled={busy || !pickedDate}
                  onClick={() => pickedDate && void confirmThenReset(toIsoDate(pickedDate))}
                  className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  Confirm this date
                </button>
                <button
                  type="button"
                  onClick={() => setShowPicker(false)}
                  className="text-xs text-slate-500 underline hover:text-slate-800"
                >
                  Back
                </button>
              </div>
            </div>
          )}

          {/* Va. Const. art. IV §6 reassurance + the official calendar link. */}
          <p data-testid="pre-session-date-reassurance" className="text-xs text-slate-500">
            Virginia's Constitution (art. IV §6) fixes the regular session to the second Wednesday
            of January. Verify a special session on the{' '}
            <a
              data-testid="pre-session-ga-calendar-link"
              href={GA_SESSION_CALENDAR_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-0.5 text-blue-700 underline hover:text-blue-900"
            >
              General Assembly session calendar
              <ExternalLink className="h-3 w-3" aria-hidden />
            </a>
            .
          </p>

          {confirmError && (
            <p role="alert" className="text-xs text-destructive">
              {confirmError}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

// ── The state-driven primary CTA ────────────────────────────────────────────

function PreSessionCtaButton({
  preview,
  generatedAt,
  busy,
  onGenerate,
}: {
  preview: PreSessionReportPreview;
  generatedAt: string | null;
  busy: boolean;
  onGenerate: () => void;
}) {
  const cta = deriveCta({
    sessionDateConfirmed: preview.sessionDateConfirmed,
    windowClosed: preview.windowClosed,
    windowEnd: preview.windowEnd,
    blockingGaps: preview.blockingGaps,
    qualifyingDonorCount: preview.qualifyingDonorCount,
    canGenerate: preview.canGenerate,
    generatedAt,
  });

  return (
    <PendingButton
      data-testid="pre-session-cta"
      pending={busy}
      icon={cta.kind === 'ready' ? <Download className="h-4 w-4" aria-hidden /> : undefined}
      disabled={!cta.enabled}
      onClick={cta.kind === 'ready' ? onGenerate : undefined}
    >
      {busy ? 'Working…' : cta.label}
    </PendingButton>
  );
}
